// Writing sessions (the Mac's SprintSheet and the summary alert; "sprint" in the code): how long and, optionally, how many
// words, and what the footer shows while it runs; the footer keeps the time; when it's over (or finished early) the summary
// says what was written and cut, and offers to extend it for a few minutes more.
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { bestRate, canExtend, sprintSummary, wordsPerMinute, SPRINT_EXTENSIONS } from '@writing-suite/core';
import { useWorkspace } from '../shell/workspace.ts';
import { lastSprintChoices, sessionDisplay, setSessionDisplay, type Writing } from '../store/writing.ts';

export const SPRINT_LENGTHS = [10, 15, 20, 25, 30, 45, 60];
const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export function SprintDialog({ writing, onClose }: { writing: Writing; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const last = lastSprintChoices();
  const [minutes, setMinutes] = useState(SPRINT_LENGTHS.includes(last.minutes) ? last.minutes : 25);
  const [goal, setGoal] = useState(last.goal > 0 ? String(last.goal) : '');
  const [display, setDisplay] = useState(sessionDisplay);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  const best = bestRate(writing.state.log, writing.projectId);
  return (
    <dialog
      ref={ref}
      className="confirm sprint-dialog"
      aria-labelledby="sprint-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const words = Number(goal.replace(/[^0-9]/g, ''));
          setSessionDisplay(display);
          onClose();
          void writing.start(minutes, words > 0 ? words : null);
        }}
      >
        <h2 id="sprint-title">Writing Session</h2>
        <div className="field-row">
          <label htmlFor="sprint-length">Length</label>
          <select id="sprint-length" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
            {SPRINT_LENGTHS.map((m) => (
              <option key={m} value={m}>
                {m} min
              </option>
            ))}
          </select>
        </div>
        <div className="field-row">
          <label htmlFor="sprint-goal">Word goal</label>
          <input id="sprint-goal" inputMode="numeric" placeholder="None" value={goal} onChange={(e) => setGoal(e.target.value)} />
        </div>
        <p className="confirm-message small">
          Words you write and words you cut are counted separately.
          {best ? ` Your best: ${Math.round(wordsPerMinute(best))} words a minute, ${DATE.format(new Date(best.started))}.` : ''}
        </p>
        <fieldset className="sprint-display">
          <label>
            <input type="checkbox" checked={display.timeLeft} onChange={(e) => setDisplay({ ...display, timeLeft: e.target.checked })} /> Show time left in
            session
          </label>
          <label>
            <input type="checkbox" checked={display.words} onChange={(e) => setDisplay({ ...display, words: e.target.checked })} /> Show words written in
            session
          </label>
        </fieldset>
        <div className="actions">
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="button primary" autoFocus>
            Start
          </button>
        </div>
      </form>
    </dialog>
  );
}

/** The finished session's summary, until dismissed. */
export function SprintSummary({ writing }: { writing: Writing }) {
  const state = useSyncExternalStore(
    (fn) => writing.subscribe(fn),
    () => writing.state,
  );
  const ref = useRef<HTMLDialogElement>(null);
  const ws = useWorkspace();
  const done = state.finished;
  useEffect(() => {
    const d = ref.current;
    if (done && d && !d.open) d.showModal();
  }, [done]);
  if (!done) return null;
  return (
    <dialog
      ref={ref}
      className="confirm sprint-summary"
      role="alertdialog"
      aria-labelledby="sprint-summary-title"
      aria-describedby="sprint-summary-text"
      onCancel={(e) => {
        e.preventDefault();
        writing.dismissSummary();
      }}
    >
      <h2 id="sprint-summary-title">Session finished</h2>
      <p id="sprint-summary-text" className="confirm-message" data-sprint-added={done.added} data-sprint-removed={done.removed}>
        {sprintSummary(done)}
      </p>
      <div className="actions">
        {canExtend(done, Date.now())
          ? SPRINT_EXTENSIONS.map((minutes) => (
              <button
                key={minutes}
                type="button"
                className="button secondary"
                data-extend={minutes}
                onClick={() => {
                  writing.extend(done, minutes);
                  ws?.focusEditor();
                }}
              >
                Extend {minutes} Minutes
              </button>
            ))
          : null}
        <button
          type="button"
          className="button primary"
          autoFocus
          onClick={() => {
            writing.dismissSummary();
            ws?.focusEditor();
          }}
        >
          OK
        </button>
      </div>
    </dialog>
  );
}
